import cn from "classnames";
import { useFieldControl } from "@/common/hooks/useFieldControl";
import { useId } from "@/common/hooks/useId";
import PropTypes from "prop-types";
import React, { forwardRef } from "react";

import styles from "./text-area.module.scss";

/**
 *
 *
 * @typedef {object} Props
 * @prop {string} name - textarea name
 * @prop {string} className - A className, used to override
 * @prop {string} rows - number of rows displayed by default
 * @prop {string} cols - number of chars displayed on one line by default
 * @prop {bool} disabled - if the textarea is disabled
 * @prop {bool} required - if the textarea is required for the parent form to complete
 * @prop {string} label - label above the textarea
 * @prop {bool} readOnly - if the textarea is readOnly
 * @prop {string | number} value - text content within the textarea field - necessary if used as controlled component
 * @prop {string | number} defaultValue - value on Render -> only used when value is not supplied and the component is uncontrolled
 * @prop {func} onChange - handler that contains the current value for use in parent component
 * @prop {string | number} placeholder - displayed in the textarea field until the user enters something
 * @prop {func} wrapperRef - ref to access by parent component
 * @prop {func} ref - ref to access the inner input. Needed to access the value when uncontrolled
 * @prop {string} [id] - an optional id to use instead of the internally generated one
 * @prop {TextArea.VALIDATION.PASS | TextArea.VALIDATION.FAIL} [validation] - visual changes based on whether the validation passed or failed
 * @prop {object} [wrapperProps] - an optional props object that will be spreadded into the wrapper component
 *
 * @returns {JSX.Element}
 */
const TextArea = forwardRef(
  (
    {
      className,
      name,
      rows,
      cols,
      disabled,
      required,
      label,
      readOnly,
      value,
      defaultValue,
      onChange,
      placeholder,
      wrapperRef,
      id,
      validation,
      wrapperProps,
      ...props
    },
    ref
  ) => {
    const computedId = useId(name);
    const { fieldControlProps, handleChange } = useFieldControl({
      onChange,
      value,
      defaultValue,
    });

    return (
      <span
        ref={wrapperRef}
        className={cn(className, styles.textAreaWrapper)}
        {...wrapperProps}
      >
        <textarea
          {...props}
          id={id || computedId}
          ref={ref}
          rows={rows}
          cols={cols}
          readOnly={readOnly}
          required={required}
          name={name}
          disabled={disabled}
          aria-label={name}
          placeholder={placeholder}
          onChange={handleChange}
          {...fieldControlProps}
          className={cn({
            [styles.validationPass]: validation === TextArea.VALIDATION.PASS,
            [styles.validationFail]: validation === TextArea.VALIDATION.FAIL,
          })}
        />
        {label && <label htmlFor={id || computedId}>{label}</label>}
      </span>
    );
  }
);

TextArea.displayName = "TextArea";

TextArea.VALIDATION = {
  FAIL: "FAIL",
  PASS: "PASS",
};

TextArea.defaultProps = {
  wrapperProps: {},
};

TextArea.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  rows: PropTypes.number,
  cols: PropTypes.number,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  wrapperRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  id: PropTypes.string,
  validation: PropTypes.oneOf([
    TextArea.VALIDATION.FAIL,
    TextArea.VALIDATION.PASS,
  ]),
  wrapperProps: PropTypes.object,
};

export default TextArea;
